/**
 * Central registry for all action definitions.
 *
 * The ActionRegistry provides a single source of truth for action definitions,
 * supporting both static definitions and dynamic factories. It handles:
 * - Registration of action definitions
 * - Resolution of dynamic labels/icons
 * - Evaluation of visibility predicates
 * - Keyboard shortcut mapping
 */

import { getShortcutDisplayString, KeyboardShortcut } from "@thenounproject/lingo-core";
import { Action, ActionContext } from "./types";
import {
  ActionDefinition,
  ActionFactory,
  ActionDeps,
  shortcutToKey,
  eventToShortcutKeys,
  resolveDynamicAttribute,
  ActionWithShortcut,
} from "./types";

import { actionDefinitions, downloadActionsFactory } from "./definitions";

/**
 * Union type for performer source - can be either:
 * - ActionDeps: new pattern where definitions have inline perform functions
 * - PerformerMap: legacy pattern where performers are registered separately
 */

class ActionRegistry {
  private static instance: ActionRegistry;

  /** Map of action ID to static definition */
  private definitions: Map<string, ActionDefinition> = new Map();

  /** Array of factory functions for dynamic actions */
  private factories: ActionFactory[] = [];

  /** Map of shortcut key string to action ID */
  private shortcutMap: Map<string, string> = new Map();

  private constructor() {}

  /**
   * Get the singleton instance of the registry.
   */
  static getInstance(): ActionRegistry {
    if (!ActionRegistry.instance) {
      ActionRegistry.instance = new ActionRegistry();
    }
    return ActionRegistry.instance;
  }

  /**
   * Register a static action definition.
   */
  register(definition: ActionDefinition): void {
    if (this.definitions.has(definition.id)) {
      console.warn(`Action "${definition.id}" is already registered. Overwriting.`);
    }

    this.definitions.set(definition.id, definition);

    // Register keyboard shortcut if present
    if (definition.shortcut) {
      const key = shortcutToKey(definition.shortcut);
      if (this.shortcutMap.has(key)) {
        const existingId = this.shortcutMap.get(key);
        console.warn(
          `Shortcut "${key}" is already registered to "${existingId}". Overwriting with "${definition.id}".`
        );
      }
      this.shortcutMap.set(key, definition.id);
    }
  }

  /**
   * Register multiple static action definitions.
   */
  registerAll(definitions: ActionDefinition[]): void {
    definitions.forEach(def => this.register(def));
  }

  /**
   * Register a factory function for dynamic actions.
   */
  registerFactory(factory: ActionFactory): void {
    this.factories.push(factory);
  }

  /**
   * Get all action definitions (both static and from factories).
   */
  getAllDefinitions(context: ActionContext): ActionDefinition[] {
    const staticDefs = Array.from(this.definitions.values());
    const dynamicDefs = this.factories.flatMap(factory => factory(context));
    return [...staticDefs, ...dynamicDefs];
  }

  /**
   * Get all enabled actions for a given context.
   * Returns Action objects ready for use in the UI.
   */
  getActions(context: ActionContext, actionDeps: ActionDeps): Action[] {
    const allDefinitions = this.getAllDefinitions(context);
    return allDefinitions
      .map(definition => this.evaluateDefinition(definition, context, actionDeps))
      .filter(Boolean);
  }

  /**
   * Get an action by its keyboard shortcut.
   * Returns null if no matching shortcut or action is disabled.
   */
  getActionByShortcut(
    event: KeyboardEvent,
    context: ActionContext,
    actionDeps: ActionDeps
  ): Action | null {
    const { keyBased, codeBased } = eventToShortcutKeys(event);

    // Try static definitions first (via shortcutMap)
    const actionId = this.shortcutMap.get(codeBased) ?? this.shortcutMap.get(keyBased);
    if (actionId) {
      const definition = this.definitions.get(actionId);
      if (definition) {
        return this.evaluateDefinition(definition, context, actionDeps);
      }
    }

    // Check dynamic definitions from factories (their shortcuts aren't in shortcutMap)
    const allDynamicDefs = this.factories.flatMap(factory => factory(context));
    for (const def of allDynamicDefs) {
      if (def.shortcut) {
        const defKey = shortcutToKey(def.shortcut);
        if (defKey === codeBased || defKey === keyBased) {
          return this.evaluateDefinition(def, context, actionDeps);
        }
      }
    }
    return null;
  }

  /**
   * Get a specific action by ID.
   */
  getActionById(actionId: string, context: ActionContext, actionDeps: ActionDeps): Action | null {
    // Check static definitions
    const staticDef = this.definitions.get(actionId);
    if (staticDef) {
      return this.evaluateDefinition(staticDef, context, actionDeps);
    }

    // Check dynamic definitions
    const allDynamicDefs = this.factories.flatMap(factory => factory(context));
    const dynamicDef = allDynamicDefs.find(d => d.id === actionId);
    if (dynamicDef) {
      return this.evaluateDefinition(dynamicDef, context, actionDeps);
    }

    return null;
  }

  /**
   * Get all registered shortcuts for display purposes.
   */
  getRegisteredShortcuts(): Map<string, KeyboardShortcut> {
    const result = new Map<string, KeyboardShortcut>();
    for (const definition of this.definitions.values()) {
      if (definition.shortcut) {
        result.set(definition.id, definition.shortcut);
      }
    }
    return result;
  }

  /**
   * Evaluate a definition against context to produce a runtime Action.
   * Returns null if the action is not enabled
   */
  private evaluateDefinition(
    definition: ActionDefinition,
    context: ActionContext,
    actionDeps: ActionDeps
  ): Action | null {
    // Determine how to get the performer function
    const performFn = () => definition.perform!(context, actionDeps);

    // Evaluate visibility predicates
    const enabled = definition.visibility.predicates.every(p => p(context));
    if (!enabled) return null;

    // Evaluate default predicates (falls back to enabled if not specified)
    const defaultPredicates = definition.visibility.defaultPredicates;
    const isDefault = defaultPredicates ? defaultPredicates.every(p => p(context)) : false;

    // Resolve dynamic values
    const label = resolveDynamicAttribute(definition.label, context);
    const icon = resolveDynamicAttribute(definition.icon, context);
    const image = resolveDynamicAttribute(definition.image, context);

    // Get shortcut display string
    const shortcutDisplay = definition.shortcut
      ? getShortcutDisplayString(definition.shortcut)
      : undefined;

    return {
      id: definition.id,
      label,
      description: definition.description,
      synonyms: definition.synonyms,
      icon,
      image,
      category: definition.category,
      enabled,
      default: isDefault,
      closeOnSelect: definition.closeOnSelect,
      clearInputOnSelect: definition.clearInputOnSelect,
      perform: performFn,
      // Extension: include shortcut for matching and display
      shortcut: definition.shortcut,
      shortcutDisplay,
    } as ActionWithShortcut;
  }

  /**
   * Clear all registrations (useful for testing).
   */
  clear(): void {
    this.definitions.clear();
    this.factories.length = 0;
    this.shortcutMap.clear();
  }

  /**
   * Check if the registry has been initialized with definitions.
   */
  get isEmpty(): boolean {
    return this.definitions.size === 0 && this.factories.length === 0;
  }

  /**
   * Get count of registered static definitions.
   */
  get definitionCount(): number {
    return this.definitions.size;
  }

  /**
   * Get count of registered factories.
   */
  get factoryCount(): number {
    return this.factories.length;
  }
}

// Track initialization
let initialized = false;

/**
 * Initialize the action registry with all definitions and factories.
 */
export function initializeRegistry(): void {
  if (initialized) return;

  // Clear first to handle HMR re-initialization (module-level `initialized`
  // resets but the singleton persists, which would duplicate factories)
  actionRegistry.clear();

  // Register static definitions
  actionRegistry.registerAll(actionDefinitions);

  // Register factories for dynamic actions
  actionRegistry.registerFactory(downloadActionsFactory);

  initialized = true;
}

// Export singleton instance
export const actionRegistry = ActionRegistry.getInstance();

// Export class for testing
export { ActionRegistry };
